import { useEffect, useState, useRef, useCallback } from 'react';

export function useInView(threshold = 0.1, root = null, rootMargin = '0px') {
  const [isInView, setIsInView] = useState(false);
  const [hasRenderedOnce, setHasRenderedOnce] = useState(false); // New state to track if it has rendered
  const observerRef = useRef(null);
  const elementRef = useRef(null);

  const setRef = useCallback((node) => {
    // If the component has already rendered once, we don't need to observe it anymore
    if (hasRenderedOnce) {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (node) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            setHasRenderedOnce(true); // Set to true once it's in view
            // Disconnect the observer as soon as it's intersected once
            if (observerRef.current) {
              observerRef.current.disconnect();
            }
          } else if (!hasRenderedOnce) { // Only set to false if it hasn't rendered yet
            setIsInView(false);
          }
        },
        { threshold, root, rootMargin }
      );
      observer.observe(node);
      observerRef.current = observer;
      elementRef.current = node;
    }
  }, [threshold, root, rootMargin, hasRenderedOnce]); // Add hasRenderedOnce to dependencies

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // Return hasRenderedOnce to control rendering in your component
  return [setRef, isInView, hasRenderedOnce];
}